// @tennis-matches/clubmeisterschaft-core – Schedule Page: placeholder → build → persist in state

import React, { useEffect, useMemo, useState } from "react";
import { useSyncExternalStore } from "react";
import { useOutletContext } from "react-router-dom";
import { getState, setState, subscribe } from "@tennis-matches/core/store";
import { __asStr } from "@tennis-matches/core/utils";
import { buildGroupsKoSchedule } from "@tennis-matches/clubmeisterschaft-core/schedule";
import { buildGroupRankings } from "@tennis-matches/clubmeisterschaft-core/ranking";
import { materializeKoMatches, resolveKoEntrants } from "@tennis-matches/clubmeisterschaft-core/ko";
import { partsToResult, validateParts, upsertMatchResult } from "@tennis-matches/clubmeisterschaft-core/results";
import { ScheduleMatchRow } from "@tennis-matches/ui-components";

function InputPair({ valueA, valueB, onChangeA, onChangeB, t }) {
  return (
    <div className="tm-entry-inputpair">
      <input className="tm-entry-input" inputMode="numeric" value={valueA ?? ""} onChange={(e) => onChangeA(e.target.value)} aria-label={t("club.entry.team1")} />
      <span className="tm-entry-sep">{t("club.entry.sep")}</span>
      <input className="tm-entry-input" inputMode="numeric" value={valueB ?? ""} onChange={(e) => onChangeB(e.target.value)} aria-label={t("club.entry.team2")} />
    </div>
  );
}

function ModeInputsTwoSets({ parts, setPart, t }) {
  return (
    <div className="tm-entry-modeinputs">
      <InputPair
        t={t}
        valueA={parts?.s1a}
        valueB={parts?.s1b}
        onChangeA={(v) => setPart("s1a", v)}
        onChangeB={(v) => setPart("s1b", v)}
      />
      <InputPair
        t={t}
        valueA={parts?.s2a}
        valueB={parts?.s2b}
        onChangeA={(v) => setPart("s2a", v)}
        onChangeB={(v) => setPart("s2b", v)}
      />
    </div>
  );
}

function getSnapshot() {
  return getState();
}

export function ClubmeisterschaftSchedulePlaceholder() {
  const outletContext = useOutletContext() || {};
  const t = outletContext.t;
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const mod = state?.modules?.club || {};
  const tournament = mod.tournament || {};
  const schedule = mod.schedule || {};
  const players = Array.isArray(tournament.players) ? tournament.players : [];
  const groupsKo = tournament.groupsKo || schedule.groupsKo || tournament?.meta?.groupsKo;

  const resultsItems = useMemo(() => {
    const raw = mod?.results?.items ?? mod?.results ?? {};
    if (Array.isArray(raw)) return Object.fromEntries((raw || []).filter((r) => r?.matchId).map((r) => [r.matchId, r]));
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  }, [mod?.results]);

  const [partsByMatchId, setPartsByMatchId] = useState({});
  const setPart = (matchId, key, value) => {
    setPartsByMatchId((prev) => ({
      ...(prev || {}),
      [matchId]: { ...((prev || {})[matchId] || {}), [key]: value },
    }));
  };

  useEffect(() => {
    if (!schedule || schedule.status !== "placeholder") return;
    if (__asStr(schedule.mode) !== "groupsKo") return;

    const built = buildGroupsKoSchedule({ players, groupsKo: schedule.groupsKo || groupsKo });
    setState((current) => {
      if (!current) return current;
      const curMod = current.modules?.club || {};
      const curSchedule = curMod.schedule || {};
      if (curSchedule.status === "built") return current;

      return {
        ...current,
        modules: {
          ...current.modules,
          club: {
            ...curMod,
            schedule: built,
          },
        },
      };
    });
  }, [schedule?.status, schedule?.mode, players, groupsKo]);

  const points = tournament?.points || { win: 2, draw: 1 };
  const groupRankings = useMemo(() => {
    if (schedule?.status !== "built") return null;
    return buildGroupRankings({
      schedule,
      results: mod.results?.items ?? mod.results ?? {},
      points,
      players,
    });
  }, [schedule, mod.results, points, players]);

  useEffect(() => {
    if (schedule?.status !== "built") return;
    if (!groupRankings) return;

    const next = resolveKoEntrants({
      schedule,
      groupRankings,
      groupsKo: schedule.config || schedule.groupsKo || groupsKo,
    });
    const prevSlots = JSON.stringify(schedule.ko?.slots || []);
    const nextSlots = JSON.stringify(next.ko?.slots || []);
    if (prevSlots === nextSlots) return;

    setState((current) => {
      const cur = current?.modules?.club?.schedule;
      if (!cur || cur.status !== "built") return current;
      return {
        ...current,
        modules: {
          ...current.modules,
          club: { ...current.modules?.club, schedule: next },
        },
      };
    });
  }, [schedule, groupRankings, groupsKo]);

  useEffect(() => {
    if (schedule?.status !== "built") return;
    if (!schedule?.ko?.rounds?.length) return;

    const nextKoMatches = materializeKoMatches({ schedule, resultsItems });
    const prevJson = JSON.stringify(schedule.koMatches || []);
    const nextJson = JSON.stringify(nextKoMatches || []);
    if (prevJson === nextJson) return;

    setState((current) => {
      const curMod = current?.modules?.club || {};
      const curSchedule = curMod.schedule || {};
      if (curSchedule.status !== "built") return current;
      return {
        ...current,
        modules: {
          ...current.modules,
          club: { ...curMod, schedule: { ...curSchedule, koMatches: nextKoMatches } },
        },
      };
    });
  }, [schedule, resultsItems]);

  const translate = (key, params) => (t ? t(key, params) : key);
  const nameById = useMemo(() => new Map((players || []).map((p) => [p.pid, __asStr(p.name)])), [players]);
  const playerName = (pid) => __asStr(nameById.get(pid) || "");

  const onSaveResult = (matchId, next) => {
    setState((current) => {
      const curMod = current?.modules?.club || {};
      const curResults = curMod.results || {};
      const curItems = curResults.items || {};
      const items = upsertMatchResult(curItems, { matchId, ...next });
      return {
        ...current,
        modules: {
          ...current.modules,
          club: {
            ...curMod,
            results: { ...curResults, items },
          },
        },
      };
    });
  };

  if (schedule?.status !== "built") {
    return (
      <div className="card" data-page="clubmeisterschaft-schedule-placeholder">
        <p className="muted">{translate("club.schedule.loading")}</p>
      </div>
    );
  }

  const groups = Array.isArray(schedule.groups) ? schedule.groups : [];
  const groupMatches = Array.isArray(schedule.groupMatches) ? schedule.groupMatches : [];
  const ko = schedule.ko || {};
  const bracketSize = ko.bracketSize ?? 0;

  return (
    <div className="card" data-page="clubmeisterschaft-schedule">
      <h2 className="card__title">{translate("club.schedule.title")}</h2>
      <section className="schedule-section">
        <h3 className="schedule-section__title">{translate("club.schedule.groups")}</h3>
        <ul className="schedule-list">
          {groups.map((g) => (
            <li key={g.id}>
              {g.name} — {translate("club.schedule.group.playersCount", { count: (g.playerIds || []).length })}
            </li>
          ))}
        </ul>
      </section>
      <section className="schedule-section">
        <h3 className="schedule-section__title">{translate("club.schedule.groupMatches")}</h3>
        <ul className="schedule-list schedule-list--matches" aria-label={translate("club.schedule.groupMatches")}>
          {(schedule.groupMatches || [])
            .slice()
            .sort((a, b) => __asStr(a.groupId).localeCompare(__asStr(b.groupId)) || (a.roundIndex - b.roundIndex))
            .map((m) => {
              const parts = partsByMatchId[m.id] || {};
              const v = validateParts(parts);
              const existing = resultsItems[m.id];
              const hasAnyParts =
                parts?.s1a !== undefined ||
                parts?.s1b !== undefined ||
                parts?.s2a !== undefined ||
                parts?.s2b !== undefined;
              const status = existing ? "entered" : hasAnyParts ? "pending" : "pending";
              const scoreText = existing ? `${existing.setsA ?? 0}:${existing.setsB ?? 0}` : "";

              const onSave = () => {
                const vv = validateParts(parts);
                if (!vv.ok) return;
                const r = partsToResult(parts);
                if (!r) return;
                setState((current) => {
                  const curMod = current?.modules?.club || {};
                  const curResults = curMod.results || {};
                  const curItems = curResults.items || {};
                  const nextItems = {
                    ...(curItems || {}),
                    [m.id]: {
                      matchId: m.id,
                      status: "confirmed",
                      ...r,
                      updatedAt: Date.now(),
                    },
                  };
                  return {
                    ...current,
                    modules: {
                      ...current.modules,
                      club: {
                        ...curMod,
                        results: { ...curResults, items: nextItems },
                      },
                    },
                  };
                });
              };

              return (
                <li key={m.id} className="schedule-list__item">
                  <ScheduleMatchRow
                    layout="card"
                    mode="entry"
                    courtLabel={m.groupId}
                    t={translate}
                    status={status}
                    scoreText={scoreText}
                    dataKey={m.id}
                    teamA={playerName(m.aId)}
                    teamB={playerName(m.bId)}
                    errorMessage={!v.ok ? translate(v.errKey) : undefined}
                    team1LabelKey="club.entry.team1"
                    team2LabelKey="club.entry.team2"
                    scoreLabelKey="club.match.sets"
                    vsKey="club.match.vs"
                    statusPendingKey="club.match.enterResult"
                    statusEnteredKey="club.match.confirmed"
                  >
                    <ModeInputsTwoSets
                      t={translate}
                      parts={parts}
                      setPart={(k, val) => setPart(m.id, k, val)}
                    />
                    <div className="tm-entry-actions">
                      <button
                        type="button"
                        className="tm-entry-savebtn"
                        disabled={!v.ok}
                        onClick={onSave}
                        aria-label={translate("club.entry.save")}
                      >
                        {translate("club.entry.save")}
                      </button>
                    </div>
                  </ScheduleMatchRow>
                </li>
              );
            })}
        </ul>
      </section>
      <section className="schedule-section">
        <h3 className="schedule-section__title">{translate("club.schedule.koMatches")}</h3>
        <ul className="schedule-list schedule-list--matches" aria-label={translate("club.schedule.koMatches")}>
          {(schedule.koMatches || []).map((m) => {
            const res = resultsItems[m.id] || null;
            const hasResult = res && (Number(res.setsA) >= 0 || Number(res.setsB) >= 0);
            const scoreText = res ? `${res.setsA ?? 0}:${res.setsB ?? 0}` : "";
            const aName = m.aId ? playerName(m.aId) : translate("club.schedule.tbd");
            const bName = m.bId ? playerName(m.bId) : translate("club.schedule.tbd");
            const isDraw = res && Number(res.setsA) === Number(res.setsB) && res.setsA !== "" && res.setsA != null;

            return (
              <li key={m.id} className="schedule-list__item">
                <ScheduleMatchRow
                  layout="card"
                  mode="entry"
                  courtLabel={translate("club.schedule.ko") + " R" + (m.roundIndex + 1)}
                  teamA={aName}
                  teamB={bName}
                  t={translate}
                  status={hasResult ? "entered" : "pending"}
                  scoreText={scoreText}
                  dataKey={m.id}
                  scoreLabelKey="club.match.sets"
                  vsKey="club.match.vs"
                  statusPendingKey={m.status === "ready" ? "club.match.enterResult" : "club.schedule.koNotReady"}
                  statusEnteredKey="club.match.confirmed"
                >
                  {m.status === "ready" ? (
                    <div className="schedule-match-result-entry">
                      <span className="muted">{translate("club.match.sets")}</span>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        value={res?.setsA ?? ""}
                        onChange={(e) =>
                          onSaveResult(m.id, {
                            setsA: Number(e.target.value) || 0,
                            setsB: Number(res?.setsB) || 0,
                            gamesA: Number(res?.gamesA) || 0,
                            gamesB: Number(res?.gamesB) || 0,
                          })
                        }
                        aria-label={translate("club.match.sets")}
                        placeholder="0"
                      />
                      <span className="muted">:</span>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        value={res?.setsB ?? ""}
                        onChange={(e) =>
                          onSaveResult(m.id, {
                            setsA: Number(res?.setsA) || 0,
                            setsB: Number(e.target.value) || 0,
                            gamesA: Number(res?.gamesA) || 0,
                            gamesB: Number(res?.gamesB) || 0,
                          })
                        }
                        aria-label={translate("club.match.sets")}
                        placeholder="0"
                      />
                      {isDraw && (
                        <span className="muted" role="alert">
                          {translate("club.entry.error.drawNotAllowed")}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="muted">{translate("club.schedule.koNotReady")}</p>
                  )}
                </ScheduleMatchRow>
              </li>
            );
          })}
        </ul>
      </section>
      {groupRankings && (
        <section className="schedule-section">
          <h3 className="schedule-section__title">{translate("club.schedule.qualifiersPreview")}</h3>
          <ul className="schedule-list">
            {groups.map((g) => {
              const rows = groupRankings[g.id]?.rows || [];
              const top2 = rows.slice(0, 2);
              return (
                <li key={g.id}>
                  {g.name}: {top2.map((row, i) => `${translate("club.schedule.place", { n: i + 1 })} ${row.name}`).join(", ") || "—"}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <p className="schedule-meta">
        {translate("club.schedule.matches")}: {groupMatches.length}
      </p>
      <p className="schedule-meta">
        {translate("club.schedule.ko")}: {bracketSize}
      </p>
    </div>
  );
}
